import {List, type Map} from 'immutable';

/**
 * Subset of the Classroom meta model methods these extensions rely on. These map
 * to dynamic JSON:API meta fields surfaced as numbers by the model.
 */
interface ClassroomMeta {
  getCountOfStudents(): number;
  getCountOfPendingClassroomInvitations(): number;
  getCountOfAssignments(): number;
}

/**
 * A related subject as accessed through the classroom's `subjects` relationship.
 */
interface ClassroomSubject {
  getId(): string;
}

/**
 * Shape of the model instance these extensions are mixed into. The extensions
 * rely on methods provided by the base ClassroomModel (and its Immutable
 * record/JSON:API resource interface).
 */
interface ClassroomContext {
  setField(field: string, value: unknown): ClassroomContext;
  get(key: 'code'): string;
  get(key: 'meta'): Map<string, unknown>;
  getIn(path: ['meta', 'count_of_students']): number | undefined;
  getIn(path: ['meta', 'count_of_questions_answered']): number | undefined;
  getIn(
    path: ['meta', 'weighted_accuracies']
  ): List<Map<string, number | string>> | undefined;
  getId(): string;
  getStatus(): string;
  getMeta(): ClassroomMeta;
  getSubjects(): List<ClassroomSubject>;
  getStudents(): List<unknown>;
  isSkipAssignmentCreation(): boolean;
  // Sibling extension methods called via `this` within these extensions.
  getCountOfStudents(): number;
}

const extensions: ThisType<ClassroomContext> = {
  /**
   * Archive a classroom by setting `status` to `archived`
   */
  archiveClassroom() {
    return this.setField('status', 'archived');
  },
  /**
   * Unarchive a classroom by setting `status` to `active`
   */
  unarchiveClassroom() {
    return this.setField('status', 'active');
  },

  getInviteCode(): string {
    return this.get('code');
  },
  resetInviteCode() {
    return this.setField('code', null);
  },

  hasSubject(id: string): boolean {
    return (
      this.getSubjects().find((subject) => {
        return subject?.getId() === id;
      }) !== undefined
    );
  },

  isArchived(): boolean {
    return this.getStatus() === 'archived';
  },

  getCountOfStudents(): number {
    return this.getIn(['meta', 'count_of_students']) || 0;
  },

  getCountOfQuestionsAnswered(): number {
    return this.getIn(['meta', 'count_of_questions_answered']) || 0;
  },
  /**
   * @todo This no longer actually contains "weighted accuracies", we use it to to grab `correct_guesses` and
   * `incorrect_guesses`. Previously, we used `weighted_correct` and `weighted_possible`. We should just get rid
   * of this completely.
   */
  getWeightedAccuracies(): List<Map<string, number | string>> {
    return this.getIn(['meta', 'weighted_accuracies']) || List();
  },

  hasStudents(): boolean {
    return this.getCountOfStudents() > 0 || this.getStudents().isEmpty() === false;
  },

  hasCreatedClassroom(): boolean {
    return Boolean(this.getId());
  },

  hasInvitedStudent(): boolean {
    return (
      this.getMeta().getCountOfStudents() > 0 ||
      this.getMeta().getCountOfPendingClassroomInvitations() > 0
    );
  },

  hasCreatedAssignment(): boolean {
    return this.isSkipAssignmentCreation() || this.getMeta().getCountOfAssignments() > 0;
  },

  isLicensed() {
    return this.get('meta').get('is_licensed') ?? false;
  }
};

export default extensions;
